import { PlusIcon } from "@heroicons/react/24/outline";
import Button from "../../components/Button";
import { useState } from "react";
import FullLoading from "../../components/FullLoading";
import CreateDocumentModal from "../../components/CreateDocumentModal";
import { motion } from "motion/react";
import useOrganizationDocuments from "./hooks/useOrganizationDocuments";
import { useSearchParams } from "react-router";
import DocumentDetailsModal from "./components/DocumentItem/components/DocumentDetailsModal";
import useDocumentDetails from "./components/DocumentItem/hooks/useDocumentDetails";
import {
  cn,
  documentTypeStringToDocumentType,
  formatDate,
} from "../../lib/utils";
import CreateSharingInvitationModal from "../../components/CreateSharingInvitationModal";

const documentTypeStringToTitle: Record<string, string> = {
  communication: "Communications",
  declaration: "Déclarations SACD",
  bordereau: "Bordereaux",
  scenario: "Scénarios",
  fiche_de_paie: "Fiches de paie",
  jour_a_jour: "Jour à jours",
  other: "Autres documents",
};

const documentTypeStringToEmptyStateMessage: Record<string, string> = {
  communication: "Pas de communications pour le moment",
  declaration: "Pas de déclarations SACD pour le moment",
  bordereau: "Pas de bordereaux pour le moment",
  scenario: "Pas de scénarios pour le moment",
  fiche_de_paie: "Pas de fiches de paie pour le moment",
  jour_a_jour: "Pas de jour à jour pour le moment",
  other: "Pas de documents pour le moment",
};

const documentTypeStringToAddActionLabel: Record<string, string> = {
  communication: "Ajouter une communication",
  declaration: "Ajouter une déclaration SACD",
  bordereau: "Ajouter un bordereau",
  scenario: "Ajouter un scénario",
  fiche_de_paie: "Ajouter une fiche de paie",
  jour_a_jour: "Ajouter un jour à jour",
  other: "Ajouter un document",
};

export default function Documents() {
  const [searchParams, setSearchParams] = useSearchParams();
  // Used from the search bar and the notification center
  const id = searchParams.get("id");
  // Used from the connected side nav bar component
  const documentType = searchParams.get("documentType");
  const [invitationModalIsOpen, setInvitationModalIsOpen] = useState(false);

  // const [activeTab, setActiveTab] = useState<Tab>(tabs[0]);
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);

  const { loading, error, createdDocuments, receivedDocuments } =
    useOrganizationDocuments({
      documentType: documentType
        ? documentTypeStringToDocumentType[documentType]
        : undefined,
    });

  const { data: preSelectedDocumentData } = useDocumentDetails(id);

  const isLoading = loading;

  const hasError = error;

  if (isLoading) return <FullLoading />;

  if (hasError) return <div>Error: {hasError?.message}</div>;

  const documentsTable = () => {
    const allDocuments = [
      ...(createdDocuments || []),
      ...(receivedDocuments || []),
    ];

    return (
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full bg-card">
          <thead className="text-secondary">
            <tr className="bg-dark border-b border-border">
              <th className="text-left p-4 text-sm font-semibold">Titre</th>
              <th className="text-left p-4 text-sm font-semibold">Type</th>
              <th className="text-left p-4 text-sm font-semibold">
                Propriétaire
              </th>
              <th className="text-left p-4 text-sm font-semibold">
                Date de création
              </th>
            </tr>
          </thead>
          <tbody>
            {allDocuments.map((document, index) => {
              return (
                <tr
                  key={document.id}
                  className={cn(
                    "cursor-pointer transition-colors border-b border-border",
                    index % 2 === 0
                      ? "bg-dark/50 hover:bg-dark/70"
                      : "bg-dark/20 hover:bg-dark/70",
                    index === allDocuments.length - 1
                      ? "border-b-0"
                      : "border-b border-border"
                  )}
                  onClick={() =>
                    setSearchParams({
                      id: document.id,
                      documentType: documentType ?? "",
                    })
                  }
                >
                  <td className="p-4">
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-primary">
                        {document.title}
                      </span>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-accent/10 text-accent">
                      {documentTypeStringToTitle[document.type.toLowerCase()] ||
                        document.type}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex flex-col">
                      <span className="text-sm text-primary">
                        {document.ownerOrganizationName}
                      </span>
                      {document.ownerUserOrganization?.name !==
                      document.ownerOrganizationName ? (
                        <span className="text-xs text-secondary">
                          {document.ownerOrganizationName}
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td className="p-4">
                    <span className="text-sm text-secondary">
                      {formatDate(document.createdAt)}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const pageHeader = () => {
    return (
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-primary">
          {documentTypeStringToTitle[documentType ?? "other"] ?? "Documents"}
        </h1>
        <Button
          variant="secondary"
          onClick={() => setIsDocumentModalOpen(true)}
          dataTestId="create-document-button"
        >
          <PlusIcon className="w-4 h-4 mr-2" />
          {documentTypeStringToAddActionLabel[documentType ?? "other"] ??
            "Ajouter un document"}
        </Button>
      </div>
    );
  };

  const pageContent = () => {
    if (receivedDocuments?.length === 0 && createdDocuments?.length === 0) {
      return (
        <div className="text-secondary text-sm">
          {documentTypeStringToEmptyStateMessage[documentType ?? "other"] ??
            "Pas de documents pour le moment"}
        </div>
      );
    }

    return documentsTable();
  };

  return (
    <motion.div
      className=" flex flex-col gap-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      data-testid="documents-page"
    >
      {preSelectedDocumentData ? (
        <DocumentDetailsModal
          isOpen={true}
          onClose={() =>
            setSearchParams({
              documentType: documentType ?? "",
            })
          }
          onInvitationModalOpen={() => {
            setInvitationModalIsOpen(true);
          }}
          document={preSelectedDocumentData.document}
        />
      ) : null}

      <CreateSharingInvitationModal
        isModalOpen={invitationModalIsOpen}
        onClose={() => setInvitationModalIsOpen(false)}
      />

      <CreateDocumentModal
        isOpen={isDocumentModalOpen}
        onClose={() => setIsDocumentModalOpen(false)}
        onSubmit={() => {
          setIsDocumentModalOpen(false);
        }}
        preSelectedDocumentType={
          documentType
            ? documentTypeStringToDocumentType[documentType]
            : undefined
        }
      />
      {pageHeader()}

      <div>{pageContent()}</div>
    </motion.div>
  );
}
